import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { pageTitle } from "@/lib/brand";


const CANONICAL = "https://smartsustainability.ai/newsletter";
const TITLE = pageTitle("The Signal Briefing", "en");
const DESCRIPTION =
  "A monthly briefing on AI, sustainability, institutional trust, and the future of human judgment — filtered from global reports, research, and emerging debates.";

export const Route = createFileRoute("/newsletter")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: CANONICAL },
      { property: "og:locale", content: "en_US" },
      { property: "og:locale:alternate", content: "tr_TR" },
    ],
    links: [
      { rel: "canonical", href: CANONICAL },
      { rel: "alternate", hreflang: "en", href: CANONICAL },
      { rel: "alternate", hreflang: "tr", href: "https://akillisurdurulebilirlik.com/bulten" },
      { rel: "alternate", hreflang: "x-default", href: "https://akillisurdurulebilirlik.com/bulten" },
    ],

  }),
  component: NewsletterPage,
});

function NewsletterPage() {
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);
  const [status, setStatus] = useState<"idle" | "loading" | "ok" | "err">("idle");
  const [message, setMessage] = useState("");

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!consent) {
      setStatus("err");
      setMessage("Please confirm consent to receive the briefing.");
      return;
    }
    setStatus("loading");
    setMessage("");
    try {
      const res = await fetch("/api/public/newsletter-subscribe", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, lang: "en" }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || "Subscription failed");
      }
      setStatus("ok");
      setMessage("You're in. Check your inbox to confirm.");
      setEmail("");
    } catch (err) {
      setStatus("err");
      setMessage(err instanceof Error ? err.message : "Something went wrong. Please try again.");
    }
  };

  return (
    <article className="mx-auto max-w-2xl px-6 py-16 md:py-24 text-center">
      <p className="text-[10px] uppercase tracking-[0.24em] text-clay">The Signal Briefing</p>
      <h1 className="mt-4 font-serif text-5xl leading-tight text-ink md:text-6xl">Signal, not noise.</h1>
      <p className="mt-6 text-lg leading-relaxed text-foreground/85 md:text-xl">
        A monthly briefing on artificial intelligence, sustainability,
        institutional trust, and the future of human judgment — filtered from
        global reports, research, and emerging debates.
      </p>

      <form onSubmit={submit} className="mt-10 mx-auto max-w-md text-left space-y-4">
        <label className="block">
          <span className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">Email</span>
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@domain.com"
            className="mt-2 w-full rounded-md border border-ink/20 bg-paper px-4 py-3 text-base text-ink focus:border-ink focus:outline-none"
          />
        </label>
        <label className="flex items-start gap-3 text-sm text-muted-foreground">
          <input
            type="checkbox"
            checked={consent}
            onChange={(e) => setConsent(e.target.checked)}
            className="mt-1"
          />
          <span>
            I agree to receive the monthly briefing by email. I can unsubscribe at
            any time. See the <a href="/privacy-policy" className="underline hover:text-clay">privacy policy</a>.
          </span>
        </label>
        <button
          type="submit"
          disabled={status === "loading"}
          className="w-full rounded-full bg-ink px-5 py-3 text-[11px] uppercase tracking-[0.2em] text-paper hover:bg-clay disabled:opacity-60 transition-colors"
        >
          {status === "loading" ? "Joining…" : "Join the Briefing"}
        </button>
        {message && (
          <p className={`text-sm ${status === "ok" ? "text-moss" : "text-clay"}`}>{message}</p>
        )}
      </form>

      <div className="mt-16 text-left space-y-4 text-[15px] leading-relaxed text-foreground/80 border-t border-ink/10 pt-10">
        <h2 className="font-serif text-2xl text-ink">What you'll receive</h2>
        <ul className="list-disc list-outside pl-6 space-y-2">
          <li>One long-form analysis or framework per month — no filler.</li>
          <li>Two or three research notes on primary sources worth reading.</li>
          <li>A short editorial note on what changed in the debate this month.</li>
          <li>No sponsored content, no upsells, no third-party sharing.</li>
        </ul>
      </div>
    </article>
  );
}
